import {
  CONFIRMATION_CODE_LENGTH,
  sanitiseConfirmationCode,
} from '@/lib/confirmation-code'
import { ErrorMessage } from '@hookform/error-message'
import { VariantProps } from 'class-variance-authority'
import React from 'react'
import {
  Control,
  Controller,
  FieldErrors,
  FieldPath,
  FieldPathValue,
  FieldValues,
} from 'react-hook-form'

import { Select } from '@droidmap/frontend-ui'
import { SliderWithInput } from '@droidmap/frontend-ui'
import { ToggleGroupEnum } from '@droidmap/frontend-ui'
import { Checkbox } from '@droidmap/frontend-ui'
import { Input } from '@droidmap/frontend-ui'
import { CredentialInput } from '@droidmap/frontend-ui'
import type {
  CredentialAutoComplete,
  NonCredentialAutoComplete,
} from '@droidmap/frontend-ui'
import { RadioButton } from '@droidmap/frontend-ui'
import { toggleVariants } from '@droidmap/frontend-ui'

type InputType =
  'text' | 'email' | 'password' | 'number' | 'search' | 'tel' | 'url'

interface BaseControllerProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> {
  name: TName
  control: Control<TFieldValues>
  label: string
  defaultValue?: FieldPathValue<TFieldValues, TName>
  errors?: FieldErrors<TFieldValues>
  disabled?: boolean
  type?: InputType
  /**
   * Browser-layer autofill hint. Password-manager *extensions* ignore
   * every `TextController` field regardless of this value (see `Input`);
   * a field that should be fillable by one uses {@link CredentialController}.
   * Credential tokens are rejected here for that reason — on an ignored
   * field they would only ever half-work.
   */
  autoComplete?: NonCredentialAutoComplete
}

/**
 * Renders a form field with error handling
 */
function FormField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  name,
  errors,
  children,
}: {
  name: TName
  errors?: FieldErrors<TFieldValues>
  children: React.ReactNode
}) {
  return (
    <div>
      {children}
      {errors && (
        <ErrorMessage
          errors={errors}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          name={name as any}
          render={({ message }) => (
            <p className="mt-1 text-sm font-bold text-danger">{message}</p>
          )}
        />
      )}
    </div>
  )
}

/**
 * Text input controller. Renders the ignored-by-password-managers `Input`,
 * which is right for every field that is not a credential.
 *
 * Confirmation codes have their own {@link ConfirmationCodeController} —
 * they need `maxLength` / `inputMode` and paste sanitisation, none of which
 * this controller forwards.
 */
export function TextController<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  name,
  control,
  defaultValue,
  label,
  errors,
  disabled,
  type = 'text',
  autoComplete,
}: BaseControllerProps<TFieldValues, TName>) {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({ field }) => (
        <FormField name={name} errors={errors}>
          <Input
            {...field}
            type={type}
            label={label}
            id={name}
            disabled={disabled}
            autoComplete={autoComplete}
          />
        </FormField>
      )}
    />
  )
}

/**
 * Confirmation-code controller props. `type` and `autoComplete` are fixed by
 * the controller, so neither can be set from a call site: `type="number"`
 * would strip a leading zero and add a spinner, and the field's browser-layer
 * hint is always `one-time-code`.
 */
type ConfirmationCodeControllerProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = Omit<BaseControllerProps<TFieldValues, TName>, 'type' | 'autoComplete'>

/**
 * Apply incoming text (a paste or a drop) to the code field's current value.
 *
 * Text carrying a whole code replaces what is already there: dropping a fresh
 * code onto a stale one is the ordinary case, and almost nobody selects the old
 * one first. Anything shorter is a partial edit, so it goes in over the
 * selection. The result is sanitised either way, so the caller never has to.
 */
function insertIntoCode(
  current: string,
  incoming: string,
  selectionStart: number | null,
  selectionEnd: number | null,
): string {
  const digits = sanitiseConfirmationCode(incoming)
  if (digits.length >= CONFIRMATION_CODE_LENGTH) return digits
  return sanitiseConfirmationCode(
    current.slice(0, selectionStart ?? current.length) +
      digits +
      current.slice(selectionEnd ?? current.length),
  )
}

/**
 * The confirmation-code field: Cognito's six-digit `ConfirmSignUp` /
 * `ForgotPassword` code, on `/confirm-signup` and `/forgot-password`.
 *
 * Beyond the `Input` ignore attributes every non-credential field gets, this
 * constrains the field's *shape* — `maxLength={6}` and `inputMode="numeric"`,
 * present at first render. Six numeric characters is a poor username
 * candidate, which is the lever left after the vendor `data-*` opt-outs alone
 * failed to stop LastPass filling the field (#2882, after #2848).
 *
 * The constraint is enforced in the handlers, not by `maxLength` alone.
 * `maxLength` truncates a paste in the DOM *before* React's `onChange` runs,
 * so the very common `" 123456\n"` drag-select from the verification email
 * would arrive as `" 12345"` — last digit silently dropped, submit failing
 * with a wrong-code error the user cannot explain. `onPaste` takes the paste
 * over instead and sanitises the text itself.
 *
 * @example
 * <ConfirmationCodeController
 *   name="confirmationCode"
 *   control={control}
 *   label="6-digit confirmation code"
 *   errors={errors}
 * />
 */
export function ConfirmationCodeController<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  name,
  control,
  defaultValue,
  label,
  errors,
  disabled,
}: ConfirmationCodeControllerProps<TFieldValues, TName>) {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({ field }) => (
        <FormField name={name} errors={errors}>
          <Input
            {...field}
            type="text"
            label={label}
            id={name}
            disabled={disabled}
            // Opts the field into iOS/Android SMS-OTP autofill.
            autoComplete="one-time-code"
            inputMode="numeric"
            maxLength={CONFIRMATION_CODE_LENGTH}
            onChange={(event) =>
              field.onChange(sanitiseConfirmationCode(event.target.value))
            }
            onPaste={(event) => {
              event.preventDefault()
              const input = event.currentTarget
              field.onChange(
                insertIntoCode(
                  String(field.value ?? ''),
                  event.clipboardData.getData('text'),
                  input.selectionStart,
                  input.selectionEnd,
                ),
              )
            }}
            // Dragged-and-dropped text is a user edit too, so `maxLength`
            // truncates it exactly as it would a paste — dropping " 123456\n"
            // would leave `12345`. The drop caret isn't exposed to the handler,
            // so the text goes in at the end rather than where it landed;
            // anything carrying a whole code replaces the field either way,
            // which is what a dropped code actually is.
            onDrop={(event) => {
              event.preventDefault()
              const current = String(field.value ?? '')
              field.onChange(
                insertIntoCode(
                  current,
                  event.dataTransfer.getData('text'),
                  current.length,
                  current.length,
                ),
              )
            }}
          />
        </FormField>
      )}
    />
  )
}

/**
 * Credential controller props: the same as a text field, but `autoComplete`
 * is required and limited to the credential tokens. `"off"` and
 * `"one-time-code"` are rejected by the type — a field wanting those is not
 * a credential and belongs on {@link TextController}.
 */
interface CredentialControllerProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> extends Omit<BaseControllerProps<TFieldValues, TName>, 'autoComplete'> {
  autoComplete: CredentialAutoComplete
}

/**
 * Credential input controller — the explicit opt-in to password managers.
 * Renders `CredentialInput`, which carries none of the vendor ignore
 * attributes, so an extension can fill sign-in and offer to save / generate
 * on sign-up and password reset. Use it for username, email and password
 * fields on auth forms and nothing else. (#2848)
 *
 * @example
 * <CredentialController
 *   name="password"
 *   control={control}
 *   label="Password"
 *   type="password"
 *   autoComplete="current-password"
 * />
 */
export function CredentialController<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  name,
  control,
  defaultValue,
  label,
  errors,
  disabled,
  type = 'text',
  autoComplete,
}: CredentialControllerProps<TFieldValues, TName>) {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({ field }) => (
        <FormField name={name} errors={errors}>
          <CredentialInput
            {...field}
            type={type}
            label={label}
            id={name}
            disabled={disabled}
            autoComplete={autoComplete}
          />
        </FormField>
      )}
    />
  )
}

/**
 * Checkbox controller
 */
export function CheckboxController<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  name,
  control,
  defaultValue,
  label,
  errors,
  disabled,
}: BaseControllerProps<TFieldValues, TName>) {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({ field }) => (
        <FormField name={name} errors={errors}>
          <Checkbox
            {...field}
            checked={field.value}
            label={label}
            id={name}
            disabled={disabled}
            onChange={(e) => {
              field.onChange(e.target.checked)
            }}
          />
        </FormField>
      )}
    />
  )
}

/**
 * Select controller props
 */
interface SelectControllerProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> extends BaseControllerProps<TFieldValues, TName> {
  options?: string[]
  optionsNamed?: Array<{ name: string; value: string }>
}

/**
 * Select controller
 */
export function SelectController<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  name,
  control,
  defaultValue,
  label,
  errors,
  options,
  optionsNamed,
  disabled,
}: SelectControllerProps<TFieldValues, TName>) {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({ field }) => (
        <FormField name={name} errors={errors}>
          <Select {...field} label={label} id={name} disabled={disabled}>
            {options?.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
            {optionsNamed?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.name}
              </option>
            ))}
          </Select>
        </FormField>
      )}
    />
  )
}

/**
 * Number controller props
 */
interface NumberControllerProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> extends BaseControllerProps<TFieldValues, TName> {
  step?: string
  min?: string
  max?: string
  className?: string
}

/**
 * Number input controller
 */
export function NumberController<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  name,
  control,
  defaultValue,
  label,
  errors,
  step,
  min,
  max,
  disabled,
  className,
}: NumberControllerProps<TFieldValues, TName>) {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({ field }) => (
        <FormField name={name} errors={errors}>
          <Input
            {...field}
            type="number"
            step={step}
            onChange={(e) =>
              field.onChange(Number(e.target.value) || e.target.value)
            }
            min={min}
            max={max}
            id={name}
            label={label}
            disabled={disabled}
            className={className}
          />
        </FormField>
      )}
    />
  )
}

/**
 * Radio group controller props
 */
interface RadioGroupControllerProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> extends BaseControllerProps<TFieldValues, TName> {
  options: Array<{ name: string; value: string }>
  /** Lay the options out in a row instead of a column. */
  orientation?: 'vertical' | 'horizontal'
}

/**
 * Radio group controller
 */
export function RadioGroupController<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  name,
  control,
  defaultValue,
  label,
  errors,
  options,
  disabled,
  orientation = 'vertical',
}: RadioGroupControllerProps<TFieldValues, TName>) {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({ field }) => (
        <FormField name={name} errors={errors}>
          <div className="space-y-2">
            {label && (
              <label className="block text-sm font-medium text-text">
                {label}
              </label>
            )}
            <div
              className={
                orientation === 'horizontal'
                  ? 'flex flex-wrap gap-x-6 gap-y-2'
                  : 'space-y-2'
              }
            >
              {options.map((option) => (
                <RadioButton
                  key={option.value}
                  id={`${name}-${option.value}`}
                  label={option.name}
                  {...field}
                  value={option.value}
                  checked={field.value === option.value}
                  disabled={disabled}
                />
              ))}
            </div>
          </div>
        </FormField>
      )}
    />
  )
}

/**
 * ToggleGroupEnum controller props
 */
interface ToggleGroupEnumControllerProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TEnum extends object,
> extends BaseControllerProps<TFieldValues, TName> {
  enumObject: TEnum
  getLabel?: (value: string) => React.ReactNode
  className?: string
  variant?: VariantProps<typeof toggleVariants>['variant']
  size?: VariantProps<typeof toggleVariants>['size']
}

/**
 * ToggleGroupEnum controller
 */
export function ToggleGroupEnumController<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TEnum extends object,
>({
  name,
  control,
  defaultValue,
  label,
  errors,
  enumObject,
  getLabel,
  className,
  variant,
  size,
  disabled,
}: ToggleGroupEnumControllerProps<TFieldValues, TName, TEnum>) {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({ field }) => (
        <FormField name={name} errors={errors}>
          <div>
            <label className="block text-sm font-medium text-text mb-2">
              {label}
            </label>
            <ToggleGroupEnum
              enumObject={enumObject}
              value={field.value}
              onValueChange={field.onChange}
              getLabel={getLabel}
              className={className}
              variant={variant}
              size={size}
              disabled={disabled}
            />
          </div>
        </FormField>
      )}
    />
  )
}

/**
 * SliderWithInput controller props
 */
interface SliderWithInputControllerProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> extends BaseControllerProps<TFieldValues, TName> {
  min?: number
  max?: number
  step?: number
  className?: string
  inputClassName?: string
  sliderClassName?: string
  inputWidth?: number
  showMinMaxLabels?: boolean
}

/**
 * SliderWithInput controller
 */
export function SliderWithInputController<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  name,
  control,
  defaultValue,
  label,
  errors,
  min,
  max,
  step,
  className,
  inputClassName,
  sliderClassName,
  inputWidth,
  disabled,
  showMinMaxLabels,
}: SliderWithInputControllerProps<TFieldValues, TName>) {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({ field }) => (
        <FormField name={name} errors={errors}>
          {/* Not {...field}: SliderWithInput is a plain function component, so
              field.ref can't attach (React warns), and it takes no name/onBlur.
              Only value/onChange below actually wire into the form. */}
          <SliderWithInput
            label={label}
            min={min}
            max={max}
            step={step}
            className={className}
            inputClassName={inputClassName}
            sliderClassName={sliderClassName}
            inputWidth={inputWidth}
            disabled={disabled}
            value={field.value}
            onChange={field.onChange}
            showMinMaxLabels={showMinMaxLabels}
          />
        </FormField>
      )}
    />
  )
}
